import React, { Component } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Pagination from '../Pagination/Pagination';
import Button from '../../components/Elements/Button/Button';
import { updateFilter, resetFilter, clearFilter, updatePagination } from '../../actions/environment';
import { intersect, unique, difference } from '../../utils/array';
import { prefix, urlify } from '../../utils/string';
import { scrollTo } from '../../utils/dom';
import s from './Filter.scss';

class Filter extends Component {
	constructor(props) {
		super(props);

		this.state = {
			categories: false,
			hash: typeof window !== 'undefined' ? window.location.hash : '',
		};

		this.filterTools = React.createRef();
	}

	componentDidMount() {
		if (this.state.hash) {
			const hashFilter = [];
			this.state.hash.slice(1).split('&').forEach((filterPart) => {
				const filterParts = filterPart.split('=');
				if (filterParts.length === 1) {
					hashFilter.push({ [filterParts[0]]: true });
				} else {
					hashFilter.push({ [filterParts[0]]: filterParts[1].split(',') });
				}
			});

			this.props.updateFilter(hashFilter, true);
			this.setState({ hash: null });
		}
	}

	handleToggleDropdown = (e) => {
		e.preventDefault();
		const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
		const positionY = (this.filterTools.current.getBoundingClientRect().top + scrollTop) - 70;

		if (Math.abs(scrollTop - positionY) > 5) {
			scrollTo(200, positionY);
		}
	}

	handleClearFilter = (e) => {
		e.preventDefault();
		this.props.clearFilter();
		this.props.updatePagination(this.props.currentPath, 1);
	}

	handleToggleGroup = (e) => {
		e.preventDefault();
		const $link = e.currentTarget;
		const whichGroup = $link.dataset.group;

		this.setState((prevState) => ({
			[whichGroup]: prevState[whichGroup] ? !prevState[whichGroup] : true,
		}));
	}

	handleFilterUpdate = (e) => {
		e.preventDefault();
		const $link = e.currentTarget;
		const action = $link.getAttribute('href');
		const filterType = action.indexOf('#') === 0 ? action.slice(1, action.indexOf('=')) : 'categories';
		const filterValue = action.indexOf('#') === 0 ? action.slice(action.indexOf('=') + 1) : action.replace(prefix(this.props.setup, ''), '');

		this.props.updateFilter([{ [filterType]: filterValue }]);
		this.props.updatePagination(this.props.currentPath, 1);
	}

	render() {
		const { ListComponent, location, data, filtering, setup, filter, currentPath } = this.props;
		const { filterArrays, filterDescriptions, localized, products } = filtering;
		const isLocalized = localized[setup.language];
		const filteredByLength = filter.filteredBy.reduce((acc, filterSegment) => (Object.keys(filterSegment)[0] !== 'sort' ? acc + 1 : acc), 0);

		if (!filter.currentFilterSegment) return null;

		// Category paths are localized.
		const categories = isLocalized ? Object.keys(filterArrays.categories).reduce((acc, category) => {
			const categoryKey = isLocalized.categories[category] ? isLocalized.categories[category].key : category;
			return ({ ...acc, [categoryKey]: [...filterArrays.categories[category]] });
		}, {}) : filterArrays.categories;

		// Localize categories
		const categoryTitles = isLocalized ? Object.keys(filterArrays.categories).reduce((acc, category) => {
			const categoryKey = isLocalized.categories[category] ? isLocalized.categories[category].key : category;
			const categoryDescription = isLocalized.categories[category] ? isLocalized.categories[category].description : filterDescriptions.categories[category];
			return ({ ...acc, [categoryKey]: categoryDescription });
		}, {}) : filterDescriptions.categories;

		// Categories that use base path.
		const relevantCategories = filter.currentFilterSegment === 'all' ? { ...categories } : Object.keys(categories).reduce((acc, category) => (category.indexOf(filter.currentFilterSegment) === 0 ? { ...acc, [category]: [...categories[category]] } : acc), {});

		/*
			Filter

			This could benefit from memoization - reselect

			Filters which don't match the base list of products are removed i.e. women men etc.
			Filters which don't match the current categories' list of products are disabled. Except categories.
		*/

		// Get base URI for filtering.
		if (data.baseUri === undefined) return null;

		let relevantBaseProducts = data.baseType === 'campaigns' ? filterArrays[data.campaignType] : filterArrays[data.baseType][data.baseUri];
		if (relevantBaseProducts === undefined) relevantBaseProducts = [];
		if (relevantBaseProducts.length === 0 && data.baseType === 'brands' && filterArrays[data.baseType][urlify(data.baseUri)]) relevantBaseProducts = filterArrays[data.baseType][urlify(data.baseUri)];
		relevantBaseProducts = relevantBaseProducts.filter((rbp) => products[rbp] !== undefined);
		if (currentPath.indexOf('archive') === -1 && categories.archive) {
			relevantBaseProducts = difference(relevantBaseProducts, categories.archive);
		}
		let relevantProducts = [...relevantBaseProducts];

		const filteredProducts = {};

		// Filter only relevant products
		filter.filteredBy.forEach((filterObj) => {
			const filterType = Object.keys(filterObj)[0];
			const filterItem = filterObj[filterType];
			if (filterItem === true || (Array.isArray(filterItem) && filterItem.length > 0)) {
				// Products are filtered
				// Need to update relevantProducts on every pass.
				switch (filterType) {
					case 'categories': {
						const concatenatedItems = unique(filterItem.reduce((acc, type) => [...(categories[type] ? categories[type] : []), ...acc], []));
						filteredProducts.categories = concatenatedItems;
						relevantProducts = intersect(relevantProducts, concatenatedItems);
						break;
					}
					case 'brands': {
						const concatenatedItems = unique(filterItem.reduce((acc, type) => [...(filterArrays.brands[type] ? filterArrays.brands[type] : []), ...acc], []));
						filteredProducts.brands = concatenatedItems;
						relevantProducts = intersect(relevantProducts, concatenatedItems);
					}
						break;
					case 'newProducts':
						filteredProducts.newProducts = filterArrays.newProducts;
						relevantProducts = intersect(relevantProducts, filterArrays.newProducts);
						break;
					case 'productsOnSale':
						filteredProducts.productsOnSale = filterArrays.productsOnSale;
						relevantProducts = intersect(relevantProducts, filterArrays.productsOnSale);
						break;
					default:
						break;
				}
			}
		});

		// Last filter is special so that the user can select multiple filters.
		let relevantFilterProductsCat = [...relevantBaseProducts];
		let relevantFilterProductsLastCat = [...relevantBaseProducts];
		let lastFilter = '';

		// Restrict filters based on final filter in filteredBy.
		filter.filteredBy.slice(1).forEach((filterObj, i) => {
			lastFilter = Object.keys(filterObj)[0];
			relevantFilterProductsCat = intersect(relevantFilterProductsCat, filteredProducts[lastFilter]);
			if (i < filter.filteredBy.length - 2) relevantFilterProductsLastCat = intersect(relevantFilterProductsLastCat, filteredProducts[lastFilter]);
		});

		let relevantFilterProducts = [...relevantBaseProducts];
		let relevantFilterProductsLast = [...relevantBaseProducts];

		// Restrict filters based on final filter in filteredBy.
		filter.filteredBy.slice(0).forEach((filterObj, i) => {
			lastFilter = Object.keys(filterObj)[0];
			relevantFilterProducts = intersect(relevantFilterProducts, filteredProducts[lastFilter]);
			if (i < filter.filteredBy.length - 1) relevantFilterProductsLast = intersect(relevantFilterProductsLast, filteredProducts[lastFilter]);
		});

		/*
			Class manipulation
		*/
		const filterToolsClass = [s.filter_tools];
		if (relevantBaseProducts.length === 0) filterToolsClass.push(s.filter_tools__empty);

		const categoryGroupClasses = [s.filtering_section_group];
		if (this.state.categories) categoryGroupClasses.push(s.filtering_section_group__open);

		// Only show the immediate child categories of whichever category we're currently in.
		// If there are none (we're on a leaf category) show its siblings instead.
		let currentCategories = [filter.currentFilterSegment !== 'all' ? filter.currentFilterSegment : ''];
		if (data.baseType === 'brands' || data.baseType === 'campaigns') currentCategories = ['shop']
		if (filter.categories.length > 0) currentCategories = filter.categories;

		const findChildren = (currentCategory) => {
			const currentDepth = currentCategory === '' ? 0 : currentCategory.split('/').length;
			return Object.keys(relevantCategories).filter((category) => (
				category.split('/').length === currentDepth + 1
				&& (currentCategory === '' || category.indexOf(`${currentCategory}/`) === 0)
				&& intersect(relevantCategories[category], relevantBaseProducts).length > 0
			));
		};

		const findSiblings = (currentCategory) => {
			if (currentCategory === '') return [];
			const currentParts = currentCategory.split('/');
			const parent = currentParts.slice(0, -1).join('/');
			return Object.keys(relevantCategories).filter((category) => (
				category.split('/').length === currentParts.length
				&& (parent === '' || category.indexOf(`${parent}/`) === 0)
				&& intersect(relevantCategories[category], relevantBaseProducts).length > 0
			));
		};

		let childCategories = unique(currentCategories.reduce((acc, currentCategory) => [...acc, ...findChildren(currentCategory)], []));
		if (childCategories.length === 0) childCategories = unique(currentCategories.reduce((acc, currentCategory) => [...acc, ...findSiblings(currentCategory)], []));

		return (
			<div className={s.filter}>
				<div className={s.filter_buttons}>
					{
						data.baseType === 'categories' && childCategories.map((category) => {
							const isSelected = filter.categories.indexOf(category) !== -1;
							const isDisabled = filteredByLength > 1 && intersect(relevantCategories[category], (lastFilter === 'categories' ? relevantFilterProductsLastCat : relevantFilterProductsCat)).length === 0;

							return <Button key={category} type="link" location={prefix(setup, category)} clickEventHandler={this.handleFilterUpdate} variantClasses={isSelected ? ['button__selected'] : []} disabled={isDisabled} text={categoryTitles[category]} />;
						})
					}
				</div>
				{
					relevantProducts.length > 0 && (
						<Pagination ListComponent={ListComponent} list={relevantProducts} cache={products} currentPath={currentPath} showAll={location.query && location.query.showAll !== undefined} />
					)
				}
				<div className={s.filter_buttons}>
					{
						(data.baseType === 'brands' || data.baseType === 'campaigns') && childCategories.map((category) => {
							const isSelected = filter.categories.indexOf(category) !== -1;
							const isDisabled = filteredByLength > 1 && intersect(relevantCategories[category], (lastFilter === 'categories' ? relevantFilterProductsLastCat : relevantFilterProductsCat)).length === 0;

							return <Button key={category} type="link" location={prefix(setup, category)} clickEventHandler={this.handleFilterUpdate} variantClasses={isSelected ? ['button__selected'] : []} disabled={isDisabled} text={categoryTitles[category]} />;
						})
					}
				</div>
			</div>
		);
	}
}

const mapState = ({ filter, location }, { ListComponent, data, filtering, setup, currentPath, productUri = false }) => ({
	filter,
	location,
	ListComponent,
	data,
	filtering,
	setup,
	currentPath,
	productUri,
});

export default hot(module)(connect(mapState, { updateFilter, resetFilter, clearFilter, updatePagination })(Filter));
